// v1.34 Intent Signal 拓客 — 1 屏专用流程
// 流程 (沿用 v1.32 Wabi-sabi 设计规范):
//   1. 输入目标市场 + 产品关键词 + 客户公司名 (可选, 用来跑 intent dorks)
//   2. 一键 → 5 个 intent dork 模板 + 默认 score
//   3. 选 1 个 dork → 实际搜 Google (Layer 5, 复用 v1.7) → 抓 10 条结果
//   4. 1 键对每条结果算 Intent Score → 排序 → 显示 A/B/C
//   5. 1 键入库 + 自动发 v1.30 初次接触邮件 (Resend)
//
// 设计原则: 1 屏 1 步, 1 人外贸视角
//   - 移动端: 步骤用 tab 切换, 桌面 1 屏显示
//   - 所有状态本地 (zustand-style useState), 不写 store
//   - 0 付费: 用现有 Dork L5 + Resend

import { useMutation, useQuery } from '@tanstack/react-query';
import { Link, createFileRoute } from '@tanstack/react-router';
import { useMemo, useState } from 'react';
import { Icon } from '../components/Icon';
import { IntentBadge, IntentScoreCard } from '../components/IntentBadge';
import { Toast } from '../components/Toast';
import { Card } from '../components/ui/Card';
import { Spinner } from '../components/ui/Spinner';
import { type IntentDork, type IntentScore, intentApi, sourcingApi } from '../lib/api';
import { useAuthStore } from '../lib/auth-store';

export const Route = createFileRoute('/sourcing/intent')({
  component: SourcingIntentPage,
});

// 导出给测试用
export { SourcingIntentPage };

type DorkHit = { title: string; url: string; snippet: string };

interface ScoredHit extends DorkHit {
  score: IntentScore;
  /** 反推出的客户名 (从 URL 域名或 title 截) */
  inferredName: string;
  /** 反推的 country (从 URL .jp / .uk 等) */
  inferredCountry: string;
}

const MARKETS = [
  { value: 'JP', label: '🇯🇵 日本', placeholder: '例: 玩具 / 手办 / コレクション' },
  { value: 'US', label: '🇺🇸 美国', placeholder: '例: toy / figurine / plush' },
  { value: 'GB', label: '🇬🇧 英国', placeholder: 'example: gift / collectible' },
  { value: 'GLOBAL', label: '🌐 全球', placeholder: 'example: collectible toy' },
];

function SourcingIntentPage() {
  const { currentCompanyId } = useAuthStore();
  const [market, setMarket] = useState('JP');
  const [product, setProduct] = useState('');
  const [company, setCompany] = useState('');
  const [selectedDorkIdx, setSelectedDorkIdx] = useState(0);
  const [scoredHits, setScoredHits] = useState<ScoredHit[]>([]);
  const [toast, setToast] = useState<{ kind: 'success' | 'error' | 'info'; msg: string } | null>(
    null,
  );

  // 1) 拿 5 个 intent dork 模板 (按 market 暂时没过滤, 5 个全列)
  const { data: intentDorksData } = useQuery({
    queryKey: ['intent-dorks', company],
    queryFn: () => intentApi.listDorks(company.trim() || undefined),
  });
  const intentDorks = intentDorksData?.dorks ?? [];

  // 2) 跑 dork search (复用现有 Layer 5)
  const searchMut = useMutation({
    mutationFn: (dork: string) => sourcingApi.search({ dork, engine: 'google', limit: 10 }),
    onSuccess: async (data) => {
      if (data.fetchStatus !== 'ok' || data.results.length === 0) {
        setToast({
          kind: 'error',
          msg:
            data.fetchStatus === 'captcha' || data.fetchStatus === 'rate_limited'
              ? 'Google 拦截, 改用 Bing/DDG 或手动跳转'
              : data.fetchStatus === 'no_results'
                ? '无结果, 改用其他 dork'
                : `抓取失败: ${data.errorMessage ?? data.fetchStatus}`,
        });
        return;
      }
      setToast({ kind: 'info', msg: `抓到 ${data.results.length} 条, 算分中...` });
      // 3) 对每条结果算 Intent Score (前端调用, 0 付费, 实时)
      //    反推客户名 (从 URL 域名截取) + country (从 TLD)
      const scored: ScoredHit[] = [];
      for (const hit of data.results) {
        const inferredName = inferCompanyName(hit.url, hit.title);
        const inferredCountry = inferCountryFromUrl(hit.url);
        try {
          const r = await intentApi.score({
            companyName: inferredName,
            country: inferredCountry,
            website: hit.url,
            dorkHits: [hit],
            decisionMakerHasLinkedin: /linkedin\.com\/(in|company)/i.test(hit.url + hit.snippet),
          });
          scored.push({ ...hit, score: r.score, inferredName, inferredCountry });
        } catch {
          // 算分失败 → 跳过, 不阻塞主流程
        }
      }
      // 按 score 降序
      scored.sort((a, b) => b.score.totalScore - a.score.totalScore);
      setScoredHits(scored);
      setToast({ kind: 'success', msg: `${scored.length} 条命中, A/B/C 已排序` });
    },
    onError: (e) => setToast({ kind: 'error', msg: (e as Error).message }),
  });

  // 4) 1 键入库 + 自动发邮件
  const importMut = useMutation({
    mutationFn: (hit: ScoredHit) =>
      intentApi.import({
        name: hit.inferredName,
        country: hit.inferredCountry,
        website: hit.url,
        product: product.trim() || 'collectible figurine',
        intentGrade: hit.score.grade,
        intentScore: hit.score,
        sendEmail: false, // 默认不发, 用户点 "入库+发邮件" 按钮再发
        emailLang: hit.inferredCountry === 'JP' ? 'ja' : 'en',
      }),
    onSuccess: (data) => {
      setToast({
        kind: 'success',
        msg: `${data.isNew ? '已新建' : '已更新'} 客户 ${data.customer.name} (grade ${data.intentGrade ?? '?'})`,
      });
    },
    onError: (e) => setToast({ kind: 'error', msg: (e as Error).message }),
  });

  const importAndEmailMut = useMutation({
    mutationFn: (hit: ScoredHit) =>
      intentApi.import({
        name: hit.inferredName,
        country: hit.inferredCountry,
        website: hit.url,
        product: product.trim() || 'collectible figurine',
        intentGrade: hit.score.grade,
        intentScore: hit.score,
        sendEmail: true,
        emailTemplate: 'cold_outreach',
        emailLang: hit.inferredCountry === 'JP' ? 'ja' : 'en',
      }),
    onSuccess: (data) => {
      const sent = data.email.sent ? '已发' : data.email.dryRun ? 'dry-run' : '未发';
      setToast({
        kind: 'success',
        msg: `${data.isNew ? '新建' : '更新'} 客户 ${data.customer.name} · 邮件 ${sent} · grade ${data.intentGrade ?? '?'}`,
      });
    },
    onError: (e) => setToast({ kind: 'error', msg: (e as Error).message }),
  });

  const selectedDork = intentDorks[selectedDorkIdx] as IntentDork | undefined;

  const stats = useMemo(() => {
    const a = scoredHits.filter((h) => h.score.grade === 'A').length;
    const b = scoredHits.filter((h) => h.score.grade === 'B').length;
    const c = scoredHits.filter((h) => h.score.grade === 'C').length;
    return { a, b, c, total: scoredHits.length };
  }, [scoredHits]);

  return (
    <div className="space-y-4 sm:space-y-5 pb-24 sm:pb-6">
      {/* 顶部 PageHeader */}
      <div className="space-y-2 animate-fade-up">
        <header>
          <h1 className="text-xl sm:text-2xl font-semibold text-ink leading-tight flex items-center gap-2">
            <Icon name="zap" size="md" className="text-accent" aria-hidden />
            Intent Signal 拓客
            <span className="text-[10px] sm:text-xs font-normal text-ink-3 ml-1">v1.34</span>
          </h1>
          <p className="text-xs sm:text-sm text-ink-3 mt-1 leading-relaxed">
            Dork 5 模板 → 实际抓 Google → A/B/C 评分 → 1 键入库 + 发邮件 (Resend)。 0 付费, 0 爬虫,
            1 屏 1 步。
          </p>
        </header>
      </div>

      {/* ① 输入区 */}
      <Card padding="md" className="animate-fade-up">
        <h2 className="text-sm font-semibold text-ink-2 mb-3 flex items-center gap-1.5">
          <span className="w-1 h-4 bg-accent rounded-sm" aria-hidden />① 输入
        </h2>
        <div className="grid grid-cols-1 sm:grid-cols-3 gap-2.5">
          <div>
            <label htmlFor="intent-market" className="text-[10px] text-ink-3 block mb-1">
              目标市场
            </label>
            <select
              id="intent-market"
              value={market}
              onChange={(e) => setMarket(e.target.value)}
              className="w-full px-2.5 py-2 border border-line rounded text-sm bg-surface"
            >
              {MARKETS.map((m) => (
                <option key={m.value} value={m.value}>
                  {m.label}
                </option>
              ))}
            </select>
          </div>
          <div>
            <label htmlFor="intent-product" className="text-[10px] text-ink-3 block mb-1">
              产品关键词
            </label>
            <input
              id="intent-product"
              type="text"
              value={product}
              onChange={(e) => setProduct(e.target.value)}
              placeholder={
                MARKETS.find((m) => m.value === market)?.placeholder ?? 'collectible toy'
              }
              className="w-full px-2.5 py-2 border border-line rounded text-sm bg-surface"
            />
          </div>
          <div>
            <label htmlFor="intent-company" className="text-[10px] text-ink-3 block mb-1">
              公司名 (可选, 用来填 dork)
            </label>
            <input
              id="intent-company"
              type="text"
              value={company}
              onChange={(e) => setCompany(e.target.value)}
              placeholder="例: Acme Toy / 株式会社トイアルファ"
              className="w-full px-2.5 py-2 border border-line rounded text-sm bg-surface"
            />
          </div>
        </div>
      </Card>

      {/* ② Intent Dorks */}
      <Card padding="md" className="animate-fade-up">
        <h2 className="text-sm font-semibold text-ink-2 mb-3 flex items-center gap-1.5">
          <span className="w-1 h-4 bg-accent rounded-sm" aria-hidden />② 选 Intent Dork (
          {intentDorks.length})
        </h2>
        <div className="grid grid-cols-1 sm:grid-cols-2 gap-2">
          {intentDorks.map((d, i) => (
            <button
              key={d.id}
              type="button"
              onClick={() => setSelectedDorkIdx(i)}
              className={`text-left p-2.5 rounded border transition-colors min-h-[64px] ${
                selectedDorkIdx === i
                  ? 'bg-accent/10 border-accent'
                  : 'bg-surface-2/40 border-line hover:border-accent/60'
              }`}
            >
              <div className="flex items-center justify-between gap-2 mb-1">
                <span className="text-xs font-semibold text-ink-2">{d.title.zh}</span>
                <span className="text-[10px] text-ink-3 px-1.5 py-0.5 rounded bg-surface-2">
                  {d.category}
                </span>
              </div>
              {/* v1.37 fix: <pre> in <button> 触屏有奇怪点击区域 + a11y 不友好 → 改 <span> + whitespace-pre-wrap */}
              <span className="text-[10px] text-ink-2 block whitespace-pre-wrap break-all leading-snug font-mono">
                {d.query}
              </span>
            </button>
          ))}
        </div>
        {selectedDork && (
          <button
            type="button"
            onClick={() => selectedDork && searchMut.mutate(selectedDork.query)}
            disabled={searchMut.isPending}
            className="mt-3 w-full sm:w-auto px-4 py-2.5 bg-accent text-white rounded-md text-sm font-medium hover:bg-accent-2 inline-flex items-center justify-center gap-1.5 min-h-[44px] disabled:opacity-50"
          >
            {searchMut.isPending ? (
              <>
                <Spinner size="sm" /> 抓取中...
              </>
            ) : (
              <>
                <Icon name="search" size="sm" aria-hidden />③ 抓 Google 前 10 条 (Layer 5)
              </>
            )}
          </button>
        )}
      </Card>

      {/* ③ 结果 — 评分后客户列表 */}
      {scoredHits.length > 0 && (
        <section aria-label="Intent 评分结果" className="animate-fade-up">
          <div className="flex items-center justify-between mb-2.5 px-1">
            <h2 className="text-sm sm:text-base font-semibold text-ink flex items-center gap-1.5">
              <span className="w-1 h-4 bg-accent rounded-sm" aria-hidden />④ 评分结果 (
              {scoredHits.length})
            </h2>
            <div className="flex items-center gap-1.5 text-[10px]">
              <span className="px-2 py-0.5 rounded-full bg-success/10 text-success font-semibold">
                🟢 A {stats.a}
              </span>
              <span className="px-2 py-0.5 rounded-full bg-warn/10 text-warn font-semibold">
                🟡 B {stats.b}
              </span>
              <span className="px-2 py-0.5 rounded-full bg-ink-3/10 text-ink-3 font-semibold">
                ⚪ C {stats.c}
              </span>
            </div>
          </div>

          <div className="space-y-2">
            {scoredHits.map((h, i) => (
              <IntentResultCard
                /* v1.37 fix: Google 同 URL 重复返回会 React key 警告 — 用 url+idx 复合 key */
                key={`${h.url}#${i}`}
                hit={h}
                importing={importMut.isPending}
                importingAndEmailing={importAndEmailMut.isPending}
                onImport={() => importMut.mutate(h)}
                onImportAndEmail={() => importAndEmailMut.mutate(h)}
              />
            ))}
          </div>
        </section>
      )}

      {/* 空态 */}
      {scoredHits.length === 0 && !searchMut.isPending && (
        <Card padding="lg" className="text-center bg-surface-2/30 border-dashed">
          <Icon name="zap" size="lg" className="text-ink-3 mx-auto mb-2" aria-hidden />
          <p className="text-sm text-ink-3">
            选 1 个 dork 模板 → 点 "③ 抓 Google" → 系统自动算分 + 排序
          </p>
          <p className="text-[10px] text-ink-3 mt-1">
            提示: Dork L5 复用 v1.7 P1, 失败时改 Bing / DDG 或手动跳转
          </p>
        </Card>
      )}

      <div className="text-center pt-2">
        <Link
          to="/sourcing"
          className="text-xs text-ink-3 hover:text-accent inline-flex items-center gap-1"
        >
          <Icon name="chevron-left" size="xs" aria-hidden />
          回到找客中心
        </Link>
      </div>

      {toast && <Toast kind={toast.kind} message={toast.msg} onClose={() => setToast(null)} />}
    </div>
  );
}

// ============ 内部: 单条结果卡 ============

function IntentResultCard({
  hit,
  importing,
  importingAndEmailing,
  onImport,
  onImportAndEmail,
}: {
  hit: ScoredHit;
  importing: boolean;
  importingAndEmailing: boolean;
  onImport: () => void;
  onImportAndEmail: () => void;
}) {
  return (
    <Card padding="sm" className="hover:border-accent/40 transition-colors">
      <div className="flex items-start gap-2.5">
        <IntentBadge
          grade={hit.score.grade}
          variant="detailed"
          recommendation={hit.score.recommendation}
        />
        <div className="flex-1 min-w-0">
          <div className="text-sm font-semibold text-ink truncate">{hit.inferredName}</div>
          {/* v1.37 fix: 去掉 break-all (跟父级 min-w-0 truncate 冲突), 改用 line-clamp-1 + truncate 让长域名不溢出 */}
          <a
            href={hit.url}
            target="_blank"
            rel="noreferrer"
            className="text-[10px] text-accent hover:underline block truncate"
            title={hit.url}
          >
            {hit.url}
          </a>
          <p className="text-[10px] text-ink-3 mt-1 line-clamp-2 leading-snug">{hit.snippet}</p>
          <p className="text-[10px] text-ink-2 mt-1.5 italic">💡 {hit.score.recommendation}</p>
        </div>
      </div>

      {/* 桌面: 进度条 / 移动: 折叠 */}
      <details className="mt-2 group">
        <summary className="text-[10px] text-ink-3 cursor-pointer hover:text-ink-2 select-none flex items-center gap-1 min-h-[28px]">
          <Icon
            name="chevron-right"
            size="xs"
            className="group-open:rotate-90 transition-transform"
            aria-hidden
          />
          3 个分项 ({hit.score.behavioral.total} / {hit.score.businessScale.total} /{' '}
          {hit.score.reachability.total})
        </summary>
        <div className="mt-2 p-2 bg-surface-2/40 rounded">
          <IntentScoreCard score={hit.score} />
        </div>
      </details>

      <div className="flex items-center gap-1.5 mt-2">
        <button
          type="button"
          onClick={onImport}
          disabled={importing || importingAndEmailing}
          className="flex-1 px-2.5 py-2 bg-surface border border-line hover:border-accent/60 rounded text-xs font-medium text-ink-2 inline-flex items-center justify-center gap-1 min-h-[36px] disabled:opacity-50"
        >
          <Icon name="user-plus" size="xs" aria-hidden />
          入库
        </button>
        <button
          type="button"
          onClick={onImportAndEmail}
          disabled={importing || importingAndEmailing}
          className="flex-1 px-2.5 py-2 bg-accent text-white hover:bg-accent-2 rounded text-xs font-medium inline-flex items-center justify-center gap-1 min-h-[36px] disabled:opacity-50"
          title="v1.30 Resend 自动发初次接触邮件 (cold_outreach 模板)"
        >
          {importingAndEmailing ? (
            <Spinner size="sm" />
          ) : (
            <Icon name="mail" size="xs" aria-hidden />
          )}
          入库 + 发邮件
        </button>
      </div>
    </Card>
  );
}

// ============ 工具: 反推公司名 / country ============

function inferCompanyName(url: string, title: string): string {
  // 优先从 title 抽 (一般是 "公司名 - 描述" 格式)
  const titleMatch = title.split(/[-–—|]/)[0]?.trim();
  if (titleMatch && titleMatch.length > 2 && titleMatch.length < 80) {
    return titleMatch;
  }
  // fallback: 从 URL 域名抽 (去掉 www. / TLD)
  try {
    const u = new URL(url);
    const host = u.hostname.replace(/^www\./, '').split('.');
    if (host.length >= 2) {
      return host[host.length - 2] ?? u.hostname;
    }
    return u.hostname;
  } catch {
    return title.slice(0, 60) || url.slice(0, 60);
  }
}

function inferCountryFromUrl(url: string): string {
  try {
    const u = new URL(url);
    const tld = u.hostname.split('.').pop()?.toLowerCase() ?? '';
    if (tld === 'jp') return 'JP';
    if (tld === 'uk' || tld === 'co.uk') return 'GB';
    if (tld === 'us' || tld === 'com') return 'US';
    if (tld === 'de') return 'DE';
    if (tld === 'cn' || tld === 'com.cn') return 'CN';
    return 'US'; // 默认
  } catch {
    return 'US';
  }
}
